/**
 * methods.js contains reusable methods for interacting with Google Places autocomplete
 * and geocoding via the backend proxy.
 */
import { get } from 'src/axios/post'

// Find item in array from geocode address_components instance
function findFormFromType (arr, type, shortForm = false) {
  const item = arr.find(item => Array.isArray(item.types) && item.types.includes(type))
  if (!item) {
    return ''
  }

  return item[shortForm ? 'short_name' : 'long_name'] || item.long_name || ''
}

// Convert SDK-style { componentRestrictions: { country: 'AU' } } to REST 'country:au'
function buildComponentsParam (componentRestrictions) {
  if (!componentRestrictions || typeof componentRestrictions !== 'object') {
    return null
  }
  const parts = Object.entries(componentRestrictions)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}:${String(v).toLowerCase()}`)
  return parts.length ? parts.join('|') : null
}

/**
 * Get prediction results from search string
 * @param searchVal
 * @param options
 */
export async function getPlacePredictions (searchVal = '', options = {}) {
  if (!searchVal) {
    return []
  }

  const params = {
    input: searchVal,
    types: Array.isArray(options.types) ? options.types.join('|') : (options.types || 'geocode')
  }
  const components = buildComponentsParam(options.componentRestrictions)
  if (components) {
    params.components = components
  }

  const response = await get('v1/maps/autocomplete', params)
  if (response.status !== 'success') {
    return []
  }
  return response.payload?.predictions || []
}

/**
 * Geocode a placeID and get all details about that place.
 *  placeID can be fetched from getPlacePredictions item
 * @param placeId
 */
export async function geocodePlaceId (placeId) {
  const response = await get('v1/maps/geocode', { place_id: placeId })
  if (response.status !== 'success') {
    return null
  }
  return response.payload?.result || null
}

/**
 * Get parsed form from geocode result for use within addresses
 * @param geocodeResult
 * @returns {{latitude: *, longitude: *, address_line_1: string, locality: (string|*), state: (string|*), postal_code: (string|*), country: (string|*)}|null}
 */
export function parseAddressComponentsAsAddressModel (geocodeResult, originalStr = '') {
  if (!geocodeResult) {
    return null
  }

  const components = geocodeResult.address_components || {}
  const resultType = geocodeResult.types || []

  let latitude = geocodeResult.geometry?.location?.lat ?? null
  let longitude = geocodeResult.geometry?.location?.lng ?? null

  // Construct address_line_1 string from partials
  let subPremise = findFormFromType(components, 'subpremise')
  let streetNumber = findFormFromType(components, 'street_number')

  // If type is "route", typically means the address may not exist and Google is falling back to generic street.
  if (resultType.length === 1 && resultType[0] === 'route' && originalStr && !streetNumber) {
    let originalAddrPremise = originalStr.split(' ')[0] ?? null
    // In this scenario, we will attempt to read the original input string and manually grab the unit+street number
    //  and bind it into the geocode result
    if (originalAddrPremise && originalAddrPremise.includes('/')) {
      originalAddrPremise = originalAddrPremise.split('/')
      if (originalAddrPremise.length === 2 && !isNaN(parseInt(originalAddrPremise[0])) && !isNaN(parseInt(originalAddrPremise[1]))) {
        subPremise = parseInt(originalAddrPremise[0])
        streetNumber = parseInt(originalAddrPremise[1])
      }
    }
    // If first portion is just a number, use that as street address
    else if (!isNaN(parseInt(originalAddrPremise))) {
      streetNumber = parseInt(originalAddrPremise)
    }

    latitude = null
    longitude = null
  }

  const streetAddress = findFormFromType(components, 'route', true)

  let addressLine1 = ''
  if (subPremise) {
    addressLine1 += subPremise
    if (streetNumber) {
      addressLine1 += '/'
    }
  }
  if (streetNumber) {
    addressLine1 += streetNumber + ' '
  }
  if (streetAddress) {
    addressLine1 += streetAddress
  }
  addressLine1 = addressLine1.trim() || '-'

  const locality = findFormFromType(components, 'sublocality') || findFormFromType(components, 'locality')
  const state = findFormFromType(components, 'postal_town', true) || findFormFromType(components, 'administrative_area_level_1', true)
  const postalCode = findFormFromType(components, 'postal_code')
  const country = findFormFromType(components, 'country')

  return {
    latitude,
    longitude,
    address_line_1: addressLine1,
    locality,
    state,
    postal_code: postalCode,
    country
  }
}
